<script setup lang="ts">
import { computed } from "vue";

export interface Item {
  title: string;
  value: string;
  disabled?: boolean;
}

export interface TabsProps {
  items: Item[];
  modelValue?: string;
}

const props = defineProps<TabsProps>();

const emit = defineEmits<{
  (event: "tabSelected", value: string): void;
  (event: "update:modelValue", value: string): void;
}>();

const tabSelected = computed({
  get: () => props.modelValue,
  set: (val: string) => emit("update:modelValue", val),
});

const onTabClicked = (val: string) => {
  tabSelected.value = val;

  emit("tabSelected", val);
};
</script>

<template>
  <div class="tabs">
    <div
      v-for="(item, index) in items"
      :key="index"
      :style="{ width: `${100 / items.length}%` }"
      :class="{
        'tabs__option--selected': tabSelected === item.value,
        'tabs__option--disabled': item.disabled,
      }"
      class="tabs__option"
      @click="!item.disabled && onTabClicked(item.value)"
    >
      <span>
        {{ item.title }}
      </span>

      <div class="tabs__option__border" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.tabs {
  @include border(2, solid, b);
  @include colors.border(primary, low);

  display: flex;
  width: 100%;
  flex-wrap: nowrap;

  &__option {
    @include sp.padding(false, 3);

    cursor: pointer;
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;

    &--selected {
      & > span {
        @include colors.color(content, high, true);
      }
      .tabs__option__border {
        @include colors.bg(primary, high);
      }
    }

    &--disabled {
      @include colors.bg(secondary, lowest);
      @include colors.border(content, low-solid);
      @include colors.color(content, mid);

      opacity: 0.6;
    }

    & > span {
      @include text.general(high, s);
      @include colors.color(content, mid);

      text-transform: capitalize;
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
    }

    &__border {
      position: absolute;
      height: 2px;
      width: 100%;
      background-color: transparent;
      bottom: -2px;
    }
  }
}
</style>
